import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Badge, Container, Alert, Spinner } from "react-bootstrap";
import CommonTable from "../components/CommonTable";
import { useNavigate } from "react-router-dom";

const getBaseUrl = () => {
    let url = process.env.REACT_APP_API_URL || "http://localhost:8000";
    url = url.replace(/\/$/, "");
    if (url.endsWith('/api')) url = url.slice(0, -4);
    return url;
};

const API_BASE = getBaseUrl();
const API_URL = `${API_BASE}/api/news`;
const IMAGE_URL = `${API_BASE}/uploads/`;

const UserManageNews = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const fullName = localStorage.getItem("fullName") || "Reporter";

    const handleAuthFailure = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("role");
        localStorage.removeItem("userId");
        localStorage.removeItem("fullName");
        localStorage.removeItem("email");
        navigate("/login");
    };

    const [newsList, setNewsList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catLoading, setCatLoading] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });

    useEffect(() => {
        if (!token) {
            handleAuthFailure();
            return;
        }
        fetchCategories();
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCategories = async () => {
        try {
            setCatLoading(true);
            const res = await fetch(`${API_BASE}/api/news/categories`);
            const result = await res.json();
            if (res.ok) {
                setCategories(result.categories || []);
            } else {
                console.error("Category fetch failed", result.message);
            }
        } catch (error) {
            console.error("Category fetch error", error);
        } finally {
            setCatLoading(false);
        }
    };

    const fetchNews = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/my-news`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const result = await res.json();

            if (res.ok) {
                setNewsList(result.data || []);
            } else if (res.status === 401) {
                handleAuthFailure();
            } else {
                setMessage({ text: result.message || "Failed to load your news.", type: "danger" });
            }
        } catch (error) {
            console.error(error);
            setMessage({ text: "Failed to load your news.", type: "danger" });
        } finally {
            setLoading(false);
        }
    };

    const getImage = (item) => {
        if (!item) {
            return "https://via.placeholder.com/100x60?text=No+Image";
        }
        if (item.image && item.image.startsWith("http")) {
            return item.image;
        }
        if (item.imagePath && item.imagePath.startsWith("http")) {
            return item.imagePath;
        }
        if (item.image && item.image.startsWith("/")) {
            return `${API_BASE}${item.image}`;
        }
        if (item.image) {
            return `${IMAGE_URL}${item.image}`;
        }
        return "https://via.placeholder.com/100x60?text=No+Image";
    };

    const getAdImage = (item) => {
        if (!item) {
            return "https://via.placeholder.com/100x60?text=No+Ad";
        }
        if (item.adImage && item.adImage.startsWith("http")) {
            return item.adImage;
        }
        if (item.adImage && item.adImage.startsWith("/")) {
            return `${API_BASE}${item.adImage}`;
        }
        if (item.adImage) {
            return `${IMAGE_URL}${item.adImage}`;
        }
        return "https://via.placeholder.com/100x60?text=No+Ad";
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this article?")) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const result = await res.json();

            if (!res.ok) {
                if (res.status === 401) {
                    handleAuthFailure();
                    return;
                }
                throw new Error(result.message || "Delete failed");
            }

            setNewsList((prev) => prev.filter((item) => item._id !== id));
            setMessage({ text: "News deleted successfully.", type: "success" });
        } catch (error) {
            console.error(error);
            setMessage({ text: error.message || "Delete failed.", type: "danger" });
        }
    };

    const handleEdit = (newsItem) => {
        setSelectedNews(newsItem);
        setShowEdit(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const formData = new FormData();
            formData.append("title", selectedNews.title || "");
            formData.append("subtitle", selectedNews.subtitle || "");
            formData.append("shortDesc", selectedNews.shortDesc || "");
            formData.append("content", selectedNews.content || "");
            formData.append("category", selectedNews.category || "");
            formData.append("location", selectedNews.location || "");
            formData.append("status", selectedNews.status || "Draft");
            formData.append("videoUrl", selectedNews.videoUrl || "");
            formData.append("breakingNews", selectedNews.breakingNews || false);

            if (selectedNews.image && selectedNews.image.size) {
                formData.append("image", selectedNews.image);
            }

            const res = await fetch(`${API_URL}/${selectedNews._id}`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const result = await res.json();
            if (!res.ok) {
                if (res.status === 401) {
                    handleAuthFailure();
                    return;
                }
                throw new Error(result.message || "Update failed");
            }

            setNewsList((prev) =>
                prev.map((item) =>
                    item._id === selectedNews._id ? result.data || selectedNews : item
                )
            );
            setShowEdit(false);
            setMessage({ text: "News updated successfully.", type: "success" });
        } catch (error) {
            console.error(error);
            setMessage({ text: error.message || "Update failed.", type: "danger" });
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { accessorKey: "title", header: "Title" },
        { accessorKey: "writer", header: "Writer" },
        { accessorKey: "category", header: "Category" },
        {
            id: "description",
            header: "Description",
            accessorFn: (row) => row.shortDesc || row.content || "",
            Cell: ({ cell }) => {
                const value = cell.getValue() || "";
                return (
                    <div style={{ maxWidth: "320px", fontSize: "13px", whiteSpace: "pre-line" }}>
                        {value.slice(0, 120)}{value.length > 120 ? "..." : ""}
                    </div>
                );
            },
        },
        {
            header: "Video",
            Cell: ({ row }) =>
                row.original.videoUrl ? (
                    <a href={row.original.videoUrl} target="_blank" rel="noreferrer">
                        ▶ Watch
                    </a>
                ) : (
                    "-"
                ),
        },
        {
            header: "Image",
            Cell: ({ row }) => (
                <img
                    src={getImage(row.original)}
                    alt="News"
                    style={{ width: "80px", height: "50px", objectFit: "cover" }}
                />
            ),
        },
        {
            header: "Ad",
            Cell: ({ row }) => (
                <img
                    src={getAdImage(row.original)}
                    alt="Ad"
                    style={{ width: "80px", height: "50px", objectFit: "cover" }}
                />
            ),
        },
        {
            accessorKey: "status",
            header: "Status",
            Cell: ({ cell }) => (
                <Badge bg={cell.getValue() === "Published" ? "success" : "warning"}>
                    {cell.getValue()}
                </Badge>
            ),
        },
        {
            header: "Actions",
            Cell: ({ row }) => (
                <div className="d-flex gap-2">
                    <Button size="sm" onClick={() => handleEdit(row.original)}>
                        Edit
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(row.original._id)}>
                        Delete
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <Container className="mt-4 mb-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                <div>
                    <h3>Your News Articles</h3>
                    <p className="text-muted mb-0">Logged in as {fullName}</p>
                </div>
                <Button variant="primary" onClick={() => navigate("/user-panel/add-news")}>Add New Article</Button>
            </div>

            {message.text && (
                <Alert variant={message.type} onClose={() => setMessage({ text: "", type: "" })} dismissible>
                    {message.text}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" />
                </div>
            ) : newsList.length === 0 ? (
                <Alert variant="info" className="text-center">
                    No articles found. <Button variant="link" onClick={() => navigate("/user-panel/add-news")}>Create one now</Button>.
                </Alert>
            ) : (
                <CommonTable columns={columns} data={newsList} isLoading={loading} />
            )}

            {selectedNews && (
                <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit News</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedNews.title || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, title: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Subtitle</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedNews.subtitle || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, subtitle: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Content</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={selectedNews.content || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, content: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select
                                value={selectedNews.category || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, category: e.target.value }))}
                                disabled={catLoading}
                            >
                                <option value="">Select category</option>
                                {categories.map((cat) => (
                                    <option key={cat._id || cat.name} value={cat.name}>
                                        {cat.group ? `${cat.group} / ${cat.name}` : cat.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Short Description</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedNews.shortDesc || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, shortDesc: e.target.value }))}
                                placeholder="Short description for the news"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedNews.location || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, location: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={selectedNews.status || "Draft"}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="Draft">Draft</option>
                                <option value="Published">Published</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Video URL</Form.Label>
                            <Form.Control
                                type="url"
                                value={selectedNews.videoUrl || ""}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, videoUrl: e.target.value }))}
                                placeholder="https://example.com/video"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Featured Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, image: e.target.files[0] }))}
                            />
                            {selectedNews.image && !selectedNews.image.size && (
                                <small className="text-muted">Current: {selectedNews.image}</small>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Breaking News"
                                checked={Boolean(selectedNews.breakingNews)}
                                onChange={(e) => setSelectedNews((prev) => ({ ...prev, breakingNews: e.target.checked }))}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEdit(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Container>
    );
};

export default UserManageNews;
